import React, { createContext, useCallback, useContext, useState } from 'react';
import { SessionInvitation } from '../components/SessionInvitationModal';

interface SessionInvitationContextType {
  currentInvitation: SessionInvitation | null;
  isModalOpen: boolean;
  showInvitation: (invitation: SessionInvitation) => void;
  acceptInvitation: (invitation: SessionInvitation) => void;
  declineInvitation: () => void;
}

const SessionInvitationContext = createContext<
  SessionInvitationContextType | undefined
>(undefined);

export const useSessionInvitation = () => {
  const context = useContext(SessionInvitationContext);
  if (context === undefined) {
    throw new Error(
      'useSessionInvitation must be used within a SessionInvitationProvider',
    );
  }
  return context;
};

interface SessionInvitationProviderProps {
  children: React.ReactNode;
  onAcceptInvitation?: (invitation: SessionInvitation) => void;
}

export const SessionInvitationProvider: React.FC<
  SessionInvitationProviderProps
> = ({ children, onAcceptInvitation }) => {
  const [currentInvitation, setCurrentInvitation] =
    useState<SessionInvitation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showInvitation = useCallback((invitation: SessionInvitation) => {
    setCurrentInvitation(invitation);
    setIsModalOpen(true);
  }, []);

  const acceptInvitation = useCallback(
    (invitation: SessionInvitation) => {
      setIsModalOpen(false);
      setCurrentInvitation(null);

      // Call the custom accept handler if provided
      if (onAcceptInvitation) {
        onAcceptInvitation(invitation);
      }
    },
    [onAcceptInvitation],
  );

  const declineInvitation = useCallback(() => {
    setIsModalOpen(false);
    setCurrentInvitation(null);
  }, []);

  const value: SessionInvitationContextType = {
    currentInvitation,
    isModalOpen,
    showInvitation,
    acceptInvitation,
    declineInvitation,
  };

  return (
    <SessionInvitationContext.Provider value={value}>
      {children}
    </SessionInvitationContext.Provider>
  );
};
